import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import './Navbar.css';
import familySafeLogo from '../assets/images/family-safe-logo.png';
import MessagePopup from './MessagePopup';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAccessTokenPayload, getCookie, signOut } from '../helper/APIClient';
import { useMenu } from "../context/useMenu";
import { usePackage } from "../context/usePackage";
import { useBranding } from "../context/BrandingContext";
import { useProfile } from "../context/ProfileContext";
import { useAuthPortal } from "../context/AuthPortalContext";
import { Menu, MenuSub } from "../helper/interface";
// import HelpPopup from './HelpPopUp';
import FeedbackPopup from './FeedbackPopup';
import GlobalCardSearch, { SearchIcon } from './GlobalCardSearch';
import { useChatWidget } from '../context/useChatWidget';
import { dispatchPageTour } from '../helper/pageTour';
import SunIcon from '../icons/default.svg?react';
import ChevronIcon from '../icons/chevron.svg?react';
import ProfileIcon from '../icons/profile.svg?react';
import EmailIcon from '../icons/email.svg?react';
import PackageIcon from '../icons/package.svg?react';
import ProfileAffiliationSummary from './ProfileAffiliationSummary';

const DESKTOP_NAV_BREAKPOINT = 1100;
const LEGACY_MENU_ROUTE_MAP: Record<string, string> = {
  "/nourl12": "/probate-exports",
};

const menuIconModules = {
  ...import.meta.glob('../icons/*.svg', { eager: true, import: 'default' }),
  ...import.meta.glob('../assets/**/*.svg', { eager: true, import: 'default' }),
};

const menuIconSrcByName = Object.entries(menuIconModules).reduce<Record<string, string>>(
  (iconMap, [path, iconSrc]) => {
    const fileName = path.split('/').pop();
    if (!fileName) {
      return iconMap;
    }

    const normalizedName = fileName.replace(/\.svg$/i, '').toLowerCase();
    iconMap[normalizedName] = iconSrc as string;
    return iconMap;
  },
  {}
);

// ─── Portal Dropdown ──────────────────────────────────────────────────────────
// Renders into document.body so it is NEVER clipped by navbar overflow/stacking.
interface PortalDropdownProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  minWidth?: number;
  offsetX?: number;
}

const PortalDropdown: React.FC<PortalDropdownProps> = ({
  anchorRef,
  children,
  align = 'left',
  minWidth = 280,
  offsetX = 0,
}) => {
  const [style, setStyle] = useState<React.CSSProperties>({ position: 'absolute', top: 0, left: 0, zIndex: 9999 });

  useEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();

    let left = rect.left + window.scrollX;
    if (align === 'center') left = rect.left + window.scrollX + rect.width / 2 - minWidth / 2;
    if (align === 'right')  left = rect.right + window.scrollX - minWidth;

    // Guard against going off-screen
    left += offsetX;
    left = Math.max(8, Math.min(left, window.innerWidth + window.scrollX - minWidth - 8));

    setStyle({
      position: 'absolute',
      top: rect.bottom + window.scrollY + 8,
      left,
      minWidth,
      zIndex: 9999,
    });
  }, [align, anchorRef, minWidth, offsetX]); // recalculate when the anchor or sizing inputs change

  return ReactDOM.createPortal(
    <div style={style} className="portal-dropdown-content">{children}</div>,
    document.body
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const Navbar: React.FC = () => {
  const { menus, loading, hasAccess } = useMenu();
  const {
    currentPackage,
  } = usePackage();
  const { branding } = useBranding();
  const { profile, avatarUrl } = useProfile();
  const { portal } = useAuthPortal();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState<string | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [loadings, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [feedbackPopup, setFeedback] = useState(false);
  // const [showHelp, setHelpPopup] = useState(false);
  // const [demoId, setDemoId] = useState<string>("");
  const chatWidget = useChatWidget();

  const [isDesktopNav, setIsDesktopNav] = useState(() => (
    typeof window === 'undefined' ? true : window.innerWidth > DESKTOP_NAV_BREAKPOINT
  ));
  const navigate = useNavigate();

  // Refs for portal anchor positioning
  const profileMenuAnchorRef = useRef<HTMLButtonElement>(null);
  const adminRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // User Info
  const email = profile?.email || decodeURIComponent(getCookie("email") ?? "");
  const fullName = profile?.fullName || decodeURIComponent(getCookie("fullName") ?? "");

  const getInitials = (value: string) =>
    value
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase() ?? "")
      .join("") || "FS";

  const initials = getInitials(fullName);

  const adminLevel = useMemo(() => {
    const payload = getAccessTokenPayload();
    const raw = payload?.["admin_level"] ?? payload?.["adminLevel"];

    const parsed =
      typeof raw === "number"
        ? raw
        : typeof raw === "string"
          ? Number.parseInt(raw, 10)
          : 0;

    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const getLogoSrc = () => {
    if (portal?.partner?.logoFile) return `/assets/logos/${portal.partner.logoFile}`;
    return familySafeLogo;
  };

  const sortedMenus = [...menus].sort((a, b) => a.sortOrder - b.sortOrder);
  const primaryMenus = sortedMenus
    .filter((menu: Menu) => menu.isProfile !== true)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const logoSrc = getLogoSrc();
  const profileMenus = menus.filter((m: Menu) => m.isProfile === true);
  const navbarContainerClassName = [
    "navbar-container",
    primaryMenus.length >= 6 ? "navbar-container--dense" : "",
    primaryMenus.length >= 10 ? "navbar-container--compact" : "",
  ].filter(Boolean).join(" ");
  const currentPath = location.pathname.toLowerCase();
  const isPageTourAvailable =
    currentPath === "/" ||
    currentPath === "/executor-management" ||
    currentPath === "/executor-portal" ||
    currentPath === "/probate-manager" ||
    currentPath === "/vault-administration" ||
    currentPath === "/reminders";

  const handlePageTour = () => {
    dispatchPageTour();
    setIsProfileOpen(false);
  };

  const resolveMenuActionUri = (actionUri?: string | null) => {
    if (!actionUri) {
      return null;
    }

    const normalizedActionUri = actionUri.startsWith("/") ? actionUri : `/${actionUri}`;
    return LEGACY_MENU_ROUTE_MAP[normalizedActionUri.toLowerCase()] ?? normalizedActionUri;
  };

  const isLegacyMenuRoute = (actionUri?: string | null) => {
    if (!actionUri) {
      return false;
    }

    const normalizedActionUri = actionUri.startsWith("/") ? actionUri : `/${actionUri}`;
    return normalizedActionUri.toLowerCase() in LEGACY_MENU_ROUTE_MAP;
  };

  const handleMenuAction = async (actionUri?: string | null) => {
    const normalizedActionUri = resolveMenuActionUri(actionUri);
    if (!normalizedActionUri) return;
    const uri = normalizedActionUri.toLowerCase();
    const closeMenus = () => {
      setIsMenuOpen(false);
      setIsProfileOpen(false);
      setIsAdminOpen(null);
    };

    if (uri.includes("/help/")) {
      chatWidget.expand();
      return;
    } else if (uri === "/feedback") {
      setFeedback(true);
      return;
    }

    if (uri.includes("nourl") || uri.includes("comingsoon")) {
      setShowPopup(true);
      return;
    }

    closeMenus();
    navigate(normalizedActionUri);
  };

  const isSpecialMenuAction = (actionUri?: string | null) => {
    const resolvedActionUri = resolveMenuActionUri(actionUri);
    if (!resolvedActionUri) {
      return false;
    }

    const uri = resolvedActionUri.toLowerCase();
    return uri.includes("/help/") || uri === "/feedback" || uri.includes("nourl") || uri.includes("comingsoon");
  };

  const canNavigateToMenuAction = (actionUri?: string | null) => {
    const normalized = resolveMenuActionUri(actionUri);
    if (!normalized) {
      return false;
    }

    if (isSpecialMenuAction(actionUri)) {
      return true;
    }

    if (isLegacyMenuRoute(actionUri)) {
      return true;
    }

    if (normalized.toLowerCase() === "/partner") {
      return hasAccess("/partner");
    }
    if ((normalized.toLowerCase() === "/admin/referral-partners" || normalized.toLowerCase() === "/admin/partner-programmes") && adminLevel < 50) {
      return false;
    }
    return hasAccess(normalized);
  };

  const buildMenuDisabledTitle = (actionUri?: string | null) => {
    const normalized = resolveMenuActionUri(actionUri);
    if (!normalized) {
      return "Unavailable.";
    }

    if (isLegacyMenuRoute(actionUri)) {
      return null;
    }

    if (normalized.toLowerCase() === "/partner") {
      if (!hasAccess("/partner")) {
        return "You don't have access to this page.";
      }

      return null;
    }
    if ((normalized.toLowerCase() === "/admin/referral-partners" || normalized.toLowerCase() === "/admin/partner-programmes") && adminLevel < 50) {
      return "Partner administration is only available for admin level 50+ users.";
    }
    return "You don't have access to this page.";
  };

  const renderIcon = (iconName?: string) => {
    const normalizedName = iconName?.trim().replace(/\.svg$/i, '').toLowerCase();

    if (!normalizedName) {
      return <SunIcon width="26" height="24" />;
    }

    const iconSrc = menuIconSrcByName[normalizedName];
    if (!iconSrc) {
      return <SunIcon width="26" height="24" />;
    }

    return (
      <img
        src={iconSrc}
        alt=""
        aria-hidden="true"
        className="nav-icon-image"
      />
    );
  };

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
    setIsProfileOpen(false);
    setIsAdminOpen(null);
    setIsSearchOpen(false);
  };

  const toggleProfile = async () => {
    if (!localStorage.getItem("salt")) {
      await signOut();
      navigate("/login", { replace: true });
      return;
    }
    setIsAdminOpen(null);
    setIsSearchOpen(false);
    setIsProfileOpen(prev => !prev);
  };

  const toggleAdmin = (id: string) => {
    setIsProfileOpen(false);
    setIsSearchOpen(false);
    setIsAdminOpen(prev => (prev === id ? null : id));
  };

  const toggleSearch = () => {
    setIsMenuOpen(false);
    setIsProfileOpen(false);
    setIsAdminOpen(null);
    setIsSearchOpen((prev) => !prev);
  };

  const handleSignOut = async () => {
    try {
      setLoading(true);
      await signOut();
      navigate("/logout", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setLoading(false);
    }
  };



  // Close all dropdowns when clicking outside portal content or triggers
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest('.portal-dropdown-content') ||
        target.closest('.profile-controls') ||
        target.closest('.admin-wrapper') ||
        target.closest('.navbar-search-button') ||
        target.closest('.global-card-search__panel')
      ) return;
      setIsProfileOpen(false);
      setIsAdminOpen(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Close on scroll/resize on desktop so portal-positioned dropdowns don't go stale.
  // On mobile, users need to scroll inside the menu drawer without collapsing submenus.
  useEffect(() => {
    if (!isDesktopNav) {
      return;
    }

    const close = () => { setIsProfileOpen(false); setIsAdminOpen(null); };
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [isDesktopNav]);

  useEffect(() => {
    const updateNavMode = () => {
      setIsDesktopNav(window.innerWidth > DESKTOP_NAV_BREAKPOINT);
    };

    updateNavMode();
    window.addEventListener('resize', updateNavMode);
    return () => window.removeEventListener('resize', updateNavMode);
  }, []);

  useEffect(() => {
    if (isDesktopNav) {
      setIsMenuOpen(false);
    }
  }, [isDesktopNav]);

  useEffect(() => {
    setIsSearchOpen(false);
  }, [location.key]);

  useEffect(() => {
    if (!(isMenuOpen && !isDesktopNav)) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isDesktopNav, isMenuOpen]);

  return (
    <nav className="navbar">
      <div className={navbarContainerClassName}>
        <div className="navbar-brand">
          <div className="logo">
            <span className="brand-text">
              <img
                src={logoSrc}
                alt={branding?.brandName || "Brand Logo"}
                onClick={() => navigate("/", { state: { resetCard: true } })}
                style={{ cursor: 'pointer' }}
              />
            </span>
          </div>
        </div>

        <div className="navbar-center">
          <div className={`navbar-nav ${isMenuOpen ? "active" : ""}`}>
            {!loading && primaryMenus.length > 0 && primaryMenus.map((menu: Menu) => {
              if (menu.subMenus && menu.subMenus.length > 0) {
                const sortedSubMenus = [...menu.subMenus].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
                const submenuContent = (
                  <div
                    className="admin-dropdown"
                    style={{ position: 'static', transform: 'none' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="admin-dropdown-content">
                      <div className="admin-menu-items one-column">
                        {sortedSubMenus.map((sub: MenuSub) => (
                          <button
                             key={sub.id}
                             className="admin-menu-btn"
                             onClick={() => {
                              if (!canNavigateToMenuAction(sub.actionUri)) {
                                return;
                              }
                              void handleMenuAction(sub.actionUri);
                              setIsMenuOpen(false);
                              setIsAdminOpen(null);
                            }}
                            disabled={!canNavigateToMenuAction(sub.actionUri)}
                            title={!canNavigateToMenuAction(sub.actionUri) ? (buildMenuDisabledTitle(sub.actionUri) ?? undefined) : undefined}
                          >
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );

                return (
                  <div
                    className="admin-wrapper"
                    key={menu.id}
                    ref={el => { adminRefs.current[menu.id] = el; }}
                  >
                    <div
                      className="nav-item"
                      onClick={(e) => { e.stopPropagation(); toggleAdmin(menu.id); }}
                    >
                      {renderIcon(menu.icon || undefined)}
                      <span className="nav-text">{menu.label}</span>
                      <span className={`chevron-icon ${isAdminOpen === menu.id ? 'open' : ''}`}>
                        <ChevronIcon width="20" height="20" />
                      </span>
                    </div>

                    {isAdminOpen === menu.id && (
                      isDesktopNav ? (
                        <PortalDropdown
                          anchorRef={{ current: adminRefs.current[menu.id] } as React.RefObject<HTMLElement>}
                          align="center"
                          minWidth={220}
                        >
                          {submenuContent}
                        </PortalDropdown>
                      ) : (
                        submenuContent
                      )
                    )}
                  </div>
                );
              }

              return (
                <div
                  key={menu.id}
                  className={`nav-item ${!canNavigateToMenuAction(menu.actionUri) ? "nav-item--disabled" : ""}`}
                  role="button"
                  aria-disabled={!canNavigateToMenuAction(menu.actionUri)}
                  title={!canNavigateToMenuAction(menu.actionUri) ? (buildMenuDisabledTitle(menu.actionUri) ?? undefined) : undefined}
                  onClick={() => {
                    if (!canNavigateToMenuAction(menu.actionUri)) {
                      return;
                    }

                    void handleMenuAction(menu.actionUri);
                    setIsMenuOpen(false);
                  }}
                >
                  {renderIcon(menu.icon || undefined)}
                  <span className="nav-text">{menu.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="navbar-actions">
          <button
            type="button"
            className={`navbar-search-button ${isSearchOpen ? "navbar-search-button--active" : ""}`}
            onClick={toggleSearch}
            aria-label="Search cards and templates"
            aria-expanded={isSearchOpen}
          >
            <SearchIcon />
          </button>
          <div className="profile-controls">
            <button
              type="button"
              className="profile-avatar"
              ref={profileMenuAnchorRef}
              onClick={(e) => { e.stopPropagation(); void toggleProfile(); }}
              aria-label="Open profile menu"
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="Profile" className="profile-avatar-image" />
              ) : (
                <div className="profile-avatar-fallback" aria-hidden="true">
                  {initials}
                </div>
              )}
            </button>

            <button
              type="button"
              className="profile-menu-trigger"
              onClick={(e) => { e.stopPropagation(); void toggleProfile(); }}
              aria-label="More profile options"
            >
              <span className="profile-menu-trigger-dot" />
              <span className="profile-menu-trigger-dot" />
              <span className="profile-menu-trigger-dot" />
            </button>
          </div>

          <button
            type="button"
            className={`burger-menu ${isMenuOpen ? "active" : ""}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <span className="burger-line" />
            <span className="burger-line" />
            <span className="burger-line" />
          </button>
        </div>
      </div>

      {!isDesktopNav && (
        <button
          type="button"
          className={`menu-overlay ${isMenuOpen ? "active" : ""}`}
          onClick={() => {
            setIsMenuOpen(false);
            setIsAdminOpen(null);
          }}
          aria-label="Close navigation menu"
        />
      )}

      {isProfileOpen && (
        <PortalDropdown anchorRef={profileMenuAnchorRef as React.RefObject<HTMLElement>} align="right" minWidth={300} offsetX={-80}>
          <div className="profile-dropdown" style={{ position: 'static' }} onClick={e => e.stopPropagation()}>
            <div className="profile-dropdown-header">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Profile" className="profile-dropdown-avatar" />
              ) : (
                <div className="profile-dropdown-avatar-fallback">{initials}</div>
              )}
              <div className="profile-details">
                <h3><ProfileIcon className="profile-icon" /><b>{fullName}</b></h3>
                <h3><EmailIcon className="profile-icon" /><b>{email}</b></h3>
                <h3><PackageIcon className="profile-icon" /><b>Package: {currentPackage?.packageName || "No package"}</b></h3>
                <ProfileAffiliationSummary profile={profile} />
              </div>
            </div>
            <hr />
            <div className="profile-submenu-scroll">
              <div className="profile-menu-items">
                <button
                  className="profile-menu-btn"
                  onClick={() => { navigate("/activity"); setIsProfileOpen(false); }}
                >
                  Activity
                </button>
              </div>
              {profileMenus && profileMenus.length > 0 && profileMenus.map((profileMenu) => (
                profileMenu.subMenus && profileMenu.subMenus.length > 0 && (
                  <div key={profileMenu.id} className="profile-menu-items">
                    {[...profileMenu.subMenus]
                      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                      .map((sub: MenuSub) => {
                        const isPageTourItem = sub.label.trim().toLowerCase() === "page tour";
                        const isPageTourDisabled = isPageTourItem && !isPageTourAvailable;
                        const isAccessDisabled = !isPageTourItem && !canNavigateToMenuAction(sub.actionUri);
                        const isDisabled = isPageTourDisabled || isAccessDisabled;
                        const disabledTitle = isPageTourDisabled
                          ? "Available only on Dashboard, Executor, Probate, Vault Admin, and Reminders pages."
                          : isAccessDisabled
                            ? buildMenuDisabledTitle(sub.actionUri)
                            : undefined;

                        return (
                          <button
                            key={sub.id}
                            className={`profile-menu-btn ${isDisabled ? "profile-menu-btn--disabled" : ""}`}
                            aria-disabled={isDisabled}
                            title={disabledTitle ?? undefined}
                            onClick={() => {
                              if (isDisabled) {
                                return;
                              }
                              if (isPageTourItem) {
                                handlePageTour();
                                return;
                              }

                              void handleMenuAction(sub.actionUri);
                              setIsProfileOpen(false);
                            }}
                          >
                            {sub.label}
                          </button>
                        );
                      })}
                  </div>
                )
              ))}
            </div>
            <button className="sign-out-btn" onClick={handleSignOut}>
              {loadings ? "Signing out..." : "sign out"}
            </button>
          </div>
        </PortalDropdown>
      )}

      <MessagePopup show={showPopup} onClose={() => setShowPopup(false)} />
      <FeedbackPopup
        show={feedbackPopup}
        onClose={() => setFeedback(false)}
        userName={fullName}
        userEmail={email}
      />
      <GlobalCardSearch
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
    </nav>
  );
};

export default Navbar;
